import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, type GoodsReceiptRow, type SellerListingRow } from '@chicare/shared';

import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { Icon } from '@/components/icons/Icon';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { AdminError } from '@/features/admin/AdminError';

/**
 * Booking deliveries in.
 *
 * ── WHAT THIS REPLACES ──────────────────────────────────────────────────────
 *
 * Typing a number into a box. Stock appeared because somebody said it had, with
 * no record of who delivered it, what it cost, what the invoice said or who
 * checked it in — so nothing downstream could be trusted either. You cannot
 * work out margin from a cost you never captured, score a supplier you never
 * named, or investigate a shortage you cannot trace to a delivery.
 *
 * ── THE BIT THAT EARNS ITS KEEP ─────────────────────────────────────────────
 *
 * The refusals. Expired and short-dated stock is rejected while the delivery
 * driver is still outside and the boxes can go back on the van. Suppliers in
 * this market move short-dated stock onto whoever is not paying attention, and
 * a pharmacy that accepts six-week stock on a product needing six months has
 * bought something it cannot legally sell for most of its life.
 *
 * ── WHY DRAFT, THEN POST ────────────────────────────────────────────────────
 *
 * A pharmacist checking twenty lines against an invoice gets interrupted — by
 * the counter, by the phone, by the driver wanting a signature. A half-typed
 * delivery that was already live would mean a buyer could order what is still
 * in a box on the floor. Nothing is sellable until the whole delivery is
 * booked in, and booking in is one-way.
 */
export function ReceivingScreen() {
  const [sector, setSector] = useState<string>('human');
  const [open, setOpen] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['seller', 'goods-receipts', sector],
    queryFn: ({ signal }) => api.goodsReceipts(sector, signal),
  });

  if (isError) {
    return (
      <AdminError title="We couldn't load your deliveries" error={error} onRetry={() => refetch()} />
    );
  }

  if (open) {
    return <ReceiptDetail reference={open} onDone={() => setOpen(null)} />;
  }

  if (starting) {
    return (
      <StartReceipt
        sector={sector}
        onCancel={() => setStarting(false)}
        onStarted={(reference) => {
          setStarting(false);
          setOpen(reference);
        }}
      />
    );
  }

  const receipts = data?.receipts ?? [];
  const sectors = data?.sectors ?? [];

  return (
    <div className="mx-auto w-full max-w-[1000px] px-4 py-6 xl:px-8">
      <header className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="m-0 font-display text-[26px] font-bold tracking-[-0.02em]">Deliveries</h1>
          <p className="m-0 mt-1 max-w-[62ch] text-[13px] leading-relaxed text-muted">
            Every delivery, with what it cost and who checked it in. Stock goes on the shelf when
            you book it in — and we refuse anything short-dated before you sign for it.
          </p>
        </div>

        <Button onClick={() => setStarting(true)}>Book in a delivery</Button>
      </header>

      {/* The wall. Two businesses that happen to share a login — a vet
          delivery must never appear on the pharmacy side. */}
      {sectors.length > 1 ? (
        <div className="mb-4 flex flex-wrap gap-1.5">
          {sectors.map((one) => (
            <button
              key={one.value}
              type="button"
              onClick={() => setSector(one.value)}
              data-testid={`receiving-sector-${one.value}`}
              className={cn(
                'min-h-9 rounded-full border px-3.5 text-[12.5px] font-semibold',
                sector === one.value
                  ? 'border-blue bg-blue text-white'
                  : 'border-line bg-white text-muted hover:text-ink',
              )}
            >
              {one.label}
            </button>
          ))}
        </div>
      ) : null}

      {data && data.drafts > 0 ? (
        <p className="m-0 mb-4 rounded-card border border-caution bg-caution-tint px-3.5 py-2.5 text-[12.5px] font-medium text-caution-ink">
          {data.drafts} {data.drafts === 1 ? 'delivery is' : 'deliveries are'} still being typed.
          Nothing on them is on sale yet.
        </p>
      ) : null}

      {isLoading ? (
        <div className="space-y-1.5">
          {Array.from({ length: 5 }, (_, i) => (
            <Skeleton key={i} className="h-[58px] w-full" radius="card" />
          ))}
        </div>
      ) : receipts.length === 0 ? (
        <EmptyState
          title="No deliveries yet"
          body="Book one in and every batch on it gets a cost, a supplier and a date — which is what makes everything else here worth reading."
          action={<Button onClick={() => setStarting(true)}>Book in a delivery</Button>}
        />
      ) : (
        <div className="overflow-hidden rounded-card border border-line bg-white">
          {receipts.map((one) => (
            <ReceiptRow key={one.reference} receipt={one} onOpen={() => setOpen(one.reference)} />
          ))}
        </div>
      )}
    </div>
  );
}

function ReceiptRow({ receipt, onOpen }: { receipt: GoodsReceiptRow; onOpen: () => void }) {
  const draft = receipt.status === 'draft';

  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid={`receipt-${receipt.reference}`}
      className={cn(
        'flex w-full min-h-11 flex-wrap items-center gap-x-3 gap-y-1 border-b border-line px-3.5 py-2.5 text-left last:border-b-0',
        draft ? 'bg-caution-tint/40 hover:bg-caution-tint/70' : 'bg-white hover:bg-paper',
      )}
    >
      <span className="min-w-0 flex-1 basis-[180px]">
        <span className="block truncate text-[13.5px] font-bold text-ink">
          {receipt.supplier ?? 'No supplier named'}
        </span>
        <span className="mt-0.5 block truncate text-[12px] text-muted">
          <span className="font-mono">{receipt.reference}</span>
          {receipt.invoice_no ? ` · invoice ${receipt.invoice_no}` : ''}
        </span>
      </span>

      <span className="shrink-0 text-[12px] text-muted lg:w-[110px]">
        {receipt.received_on
          ? new Date(receipt.received_on).toLocaleDateString('en-NG', {
              day: 'numeric',
              month: 'short',
              year: 'numeric',
            })
          : ''}
      </span>

      <span className="shrink-0 text-[12px] text-muted lg:w-[84px] lg:text-right">
        {receipt.line_count} line{receipt.line_count === 1 ? '' : 's'}
      </span>

      <span className="shrink-0 font-mono text-[13px] font-bold tabular-nums text-ink lg:w-[110px] lg:text-right">
        {receipt.total_cost_display}
      </span>

      <span className="flex shrink-0 items-center lg:w-[112px]">
        {draft ? (
          <span className="rounded-pill bg-caution-tint px-2 py-[3px] text-[11px] font-bold text-caution-ink">
            Still typing
          </span>
        ) : (
          <span className="rounded-pill bg-paper px-2 py-[3px] text-[11px] font-medium text-muted">
            On the shelf
          </span>
        )}
      </span>

      <Icon name="chevron" size={15} strokeWidth={2} className="hidden shrink-0 text-muted lg:block" />
    </button>
  );
}

// ── Starting one ────────────────────────────────────────────────────────────

function StartReceipt({
  sector,
  onCancel,
  onStarted,
}: {
  sector: string;
  onCancel: () => void;
  onStarted: (reference: string) => void;
}) {
  const [invoice, setInvoice] = useState('');
  const [receivedOn, setReceivedOn] = useState(todayLocal());
  const [note, setNote] = useState('');

  const start = useMutation({
    mutationFn: () =>
      api.startGoodsReceipt({
        sector,
        invoice_no: invoice,
        received_on: receivedOn,
        note,
      }),
    onSuccess: (receipt) => onStarted(receipt.reference),
  });

  const error = start.error instanceof ApiError ? start.error : null;

  return (
    <div className="mx-auto w-full max-w-[560px] px-4 py-6 xl:px-8">
      <BackLink onClick={onCancel} label="All deliveries" />

      <h1 className="m-0 font-display text-[22px] font-bold tracking-[-0.02em]">
        Book in a delivery
      </h1>
      <p className="m-0 mt-1 text-[13px] leading-relaxed text-muted">
        Start with what the invoice says. You will add each batch next, and nothing goes on sale
        until the whole delivery is booked in.
      </p>

      <div className="mt-4 space-y-3 rounded-card border border-line bg-white p-4 sm:p-5">
        <Input
          label="Invoice number"
          value={invoice}
          placeholder="INV-00482"
          mono
          error={error?.fieldErrors.invoice_no?.[0] ?? null}
          onChange={(event) => setInvoice(event.target.value)}
        />

        <Input
          label="When did it arrive?"
          type="date"
          value={receivedOn}
          max={todayLocal()}
          error={error?.fieldErrors.received_on?.[0] ?? null}
          onChange={(event) => setReceivedOn(event.target.value)}
        />

        <Input
          label="Anything worth noting (optional)"
          value={note}
          placeholder="Two boxes crushed in transit — driver signed for it"
          onChange={(event) => setNote(event.target.value)}
        />
      </div>

      {error && !error.fieldErrors.received_on ? (
        <p role="alert" className="m-0 mt-3 text-[12.5px] font-medium text-danger">
          {error.message}
        </p>
      ) : null}

      <div className="mt-5 flex flex-wrap gap-2">
        <Button disabled={start.isPending} onClick={() => start.mutate()}>
          {start.isPending ? 'Starting…' : 'Next: what arrived'}
        </Button>
        <Button variant="grey" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

// ── The delivery itself ─────────────────────────────────────────────────────

function ReceiptDetail({ reference, onDone }: { reference: string; onDone: () => void }) {
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['seller', 'goods-receipt', reference],
    queryFn: ({ signal }) => api.goodsReceipt(reference, signal),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['seller', 'goods-receipt', reference] });
    void queryClient.invalidateQueries({ queryKey: ['seller', 'goods-receipts'] });
  };

  const post = useMutation({
    mutationFn: () => api.postGoodsReceipt(reference),
    onSuccess: () => {
      invalidate();
      void queryClient.invalidateQueries({ queryKey: ['seller', 'listings'] });
    },
  });

  const removeLine = useMutation({
    mutationFn: (id: number) => api.removeGoodsReceiptLine(reference, id),
    onSuccess: invalidate,
  });

  const error = post.error instanceof ApiError ? post.error : null;

  if (isLoading || !data) {
    return (
      <div className="mx-auto w-full max-w-[760px] space-y-3 px-4 py-6">
        <Skeleton className="h-7 w-48" />
        <Skeleton className="h-40 w-full" radius="card" />
      </div>
    );
  }

  const draft = data.status === 'draft';

  return (
    <div className="mx-auto w-full max-w-[760px] px-4 py-6 xl:px-8">
      <BackLink onClick={onDone} label="All deliveries" />

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="m-0 font-display text-[22px] font-bold tracking-[-0.02em]">
            {data.supplier ?? 'Delivery'}
          </h1>
          <p className="m-0 mt-0.5 font-mono text-[11.5px] text-muted">
            {data.reference}
            {data.invoice_no ? ` · invoice ${data.invoice_no}` : ''}
            {data.received_on ? ` · ${data.received_on}` : ''}
          </p>
        </div>

        <span
          className={cn(
            'shrink-0 rounded-pill px-2.5 py-1 text-[11px] font-bold',
            draft ? 'bg-caution-tint text-caution-ink' : 'bg-paper text-muted',
          )}
        >
          {draft ? 'Still typing' : 'On the shelf'}
        </span>
      </div>

      {/* Every refusal from the server, shown together: a pharmacist at a door
          with a driver wants every problem at once, not one per attempt. */}
      {error ? (
        <div
          role="alert"
          data-testid="receipt-refusals"
          className="mt-4 rounded-card border border-danger bg-danger-tint px-4 py-3"
        >
          <p className="m-0 text-[13px] font-bold text-danger">
            This delivery was not booked in.
          </p>
          <ul className="m-0 mt-1.5 list-disc space-y-0.5 pl-4">
            {Object.values(error.fieldErrors).flat().length > 0 ? (
              Object.values(error.fieldErrors)
                .flat()
                .map((message, i) => (
                  <li key={i} className="text-[12.5px] leading-snug text-danger">
                    {message}
                  </li>
                ))
            ) : (
              <li className="text-[12.5px] leading-snug text-danger">{error.message}</li>
            )}
          </ul>
        </div>
      ) : null}

      <div className="mt-4 overflow-hidden rounded-card border border-line bg-white">
        {data.lines.length === 0 ? (
          <p className="m-0 px-4 py-6 text-center text-[13px] text-muted">
            Nothing added yet. Add each batch as you check it against the invoice.
          </p>
        ) : (
          data.lines.map((line) => (
            <div
              key={line.id}
              className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-line px-3.5 py-2.5 last:border-b-0"
            >
              <span className="min-w-0 flex-1 basis-[170px]">
                <span className="block truncate text-[13px] font-semibold text-ink">
                  {line.product ?? 'Product'}
                </span>
                <span className="mt-0.5 block font-mono text-[11.5px] text-muted">
                  batch {line.batch_no} · exp {line.expiry_date}
                </span>

                {/* Food safety, at the moment it can still be acted on. */}
                {line.withdrawal.length > 0 ? (
                  <span className="mt-1 flex flex-wrap gap-1">
                    {line.withdrawal.map((one) => (
                      <span
                        key={one.label}
                        className="rounded-pill bg-caution-tint px-1.5 py-[1px] text-[10.5px] font-semibold text-caution-ink"
                      >
                        {one.label}: {one.value}
                      </span>
                    ))}
                  </span>
                ) : null}
              </span>

              <span className="shrink-0 font-mono text-[12.5px] tabular-nums text-ink">
                {line.quantity} ×
              </span>
              <span className="shrink-0 font-mono text-[12.5px] tabular-nums text-muted">
                {line.unit_cost_display}
              </span>
              <span className="shrink-0 font-mono text-[13px] font-bold tabular-nums text-ink">
                {line.line_cost_display}
              </span>

              {draft ? (
                <button
                  type="button"
                  aria-label="Remove this line"
                  onClick={() => removeLine.mutate(line.id)}
                  className="grid h-11 w-11 shrink-0 place-items-center bg-transparent text-muted hover:text-danger"
                >
                  <Icon name="trash" size={15} strokeWidth={2} />
                </button>
              ) : null}
            </div>
          ))
        )}
      </div>

      {data.lines.length > 0 ? (
        <p className="m-0 mt-2 text-right font-mono text-[13px] font-bold tabular-nums text-ink">
          {data.total_units || data.lines.reduce((sum, l) => sum + l.quantity, 0)} units ·{' '}
          {data.total_cost_display}
        </p>
      ) : null}

      {draft ? (
        <>
          {adding ? (
            <AddLine
              reference={reference}
              sector={data.sector}
              onDone={() => {
                setAdding(false);
                invalidate();
              }}
            />
          ) : (
            <Button variant="grey" className="mt-4" onClick={() => setAdding(true)}>
              Add a batch
            </Button>
          )}

          <div className="mt-6 border-t border-line pt-4">
            <Button
              disabled={post.isPending || data.lines.length === 0}
              onClick={() => post.mutate()}
            >
              {post.isPending ? 'Booking in…' : 'Book it in'}
            </Button>
            <p className="m-0 mt-2 text-[11.5px] leading-snug text-faint">
              This puts everything above on your shelf and on sale. It cannot be undone — a mistake
              is corrected by writing the stock off with a reason.
            </p>
          </div>
        </>
      ) : null}
    </div>
  );
}

// ── One batch on the delivery ───────────────────────────────────────────────

function AddLine({
  reference,
  sector,
  onDone,
}: {
  reference: string;
  sector: string;
  onDone: () => void;
}) {
  const [listingId, setListingId] = useState<number | null>(null);
  const [batchNo, setBatchNo] = useState('');
  const [expiry, setExpiry] = useState('');
  const [quantity, setQuantity] = useState('');
  const [cost, setCost] = useState('');

  const { data: listings } = useQuery({
    queryKey: ['seller', 'listings'],
    queryFn: ({ signal }) => api.sellerListings(signal),
  });

  // Only this side of the wall. A vet delivery must not even OFFER a human
  // medicine in its picker.
  const options = useMemo(
    () => (listings?.listings ?? []).filter((one) => one.product.sector === sector),
    [listings, sector],
  );

  const add = useMutation({
    mutationFn: () =>
      api.addGoodsReceiptLine(reference, {
        listing_id: listingId!,
        batch_no: batchNo.trim(),
        expiry_date: expiry,
        quantity: Number(quantity),
        // Typed in naira, stored in kobo. Nothing downstream divides by 100.
        unit_cost_kobo: Math.round(Number(cost || 0) * 100),
      }),
    onSuccess: onDone,
  });

  const error = add.error instanceof ApiError ? add.error : null;
  const ready = listingId !== null && batchNo.trim() !== '' && expiry !== '' && Number(quantity) > 0;

  return (
    <div className="mt-4 rounded-card border border-blue bg-blue-tint/30 p-4">
      <p className="m-0 mb-3 font-mono text-[10px] uppercase tracking-[0.12em] text-muted">
        What arrived
      </p>

      <label className="block">
        <span className="mb-1.5 block text-[12.5px] font-semibold text-ink">Product</span>
        <select
          value={listingId ?? ''}
          data-testid="receipt-line-product"
          onChange={(event) => setListingId(event.target.value ? Number(event.target.value) : null)}
          className="min-h-11 w-full rounded-btn border border-line bg-white px-3 text-[14px] text-ink outline-none focus:border-blue"
        >
          <option value="">Choose a product…</option>
          {options.map((one: SellerListingRow) => (
            <option key={one.id} value={one.id}>
              {one.product.name}
            </option>
          ))}
        </select>
      </label>

      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <Input
          label="Batch number"
          mono
          value={batchNo}
          placeholder="AMX-240817"
          error={error?.fieldErrors.batch_no?.[0] ?? null}
          onChange={(event) => setBatchNo(event.target.value)}
        />

        <Input
          label="Expiry date"
          type="date"
          value={expiry}
          error={error?.fieldErrors.expiry_date?.[0] ?? null}
          onChange={(event) => setExpiry(event.target.value)}
        />

        <Input
          label="How many"
          type="number"
          inputMode="numeric"
          min={1}
          value={quantity}
          error={error?.fieldErrors.quantity?.[0] ?? null}
          onChange={(event) => setQuantity(event.target.value)}
        />

        <Input
          label="Cost each (₦)"
          type="number"
          inputMode="decimal"
          min={0}
          step="0.01"
          value={cost}
          hint="What you paid, not what you sell it for."
          onChange={(event) => setCost(event.target.value)}
        />
      </div>

      {error && Object.keys(error.fieldErrors).length === 0 ? (
        <p role="alert" className="m-0 mt-2.5 text-[12.5px] font-medium text-danger">
          {error.message}
        </p>
      ) : null}

      <div className="mt-3.5 flex flex-wrap gap-2">
        <Button size="sm" disabled={!ready || add.isPending} onClick={() => add.mutate()}>
          {add.isPending ? 'Adding…' : 'Add it'}
        </Button>
        <Button size="sm" variant="grey" onClick={onDone}>
          Done adding
        </Button>
      </div>
    </div>
  );
}

/**
 * Today, where the person is standing.
 *
 * `toISOString()` is UTC, so for most of the day it names a different date
 * than the pharmacist's calendar does — which defaulted a delivery to tomorrow
 * and then set `max` to a day the server would refuse. A date field about a
 * van that arrived this morning has to agree with the wall clock.
 */
function todayLocal(): string {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');

  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

function BackLink({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="mb-4 inline-flex min-h-11 items-center gap-1.5 bg-transparent p-0 text-[13px] font-semibold text-muted"
    >
      <Icon name="back" size={15} strokeWidth={2} />
      {label}
    </button>
  );
}
